import { 
  BarChart as BChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,ResponsiveContainer
} from 'recharts';
import { useTheme } from 'styled-components';
import {formatAxisValue,getMultiBarColor} from './utils';
import {formatCellValue} from './utils';

const CustomLegend = ({ payload, widget, statusColors }: any) => {

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      padding: '12px 0',
      bottom:'-10px',
      height:'10px'
    }}>
      {payload?.map((entry: any, index: number) => {
        const bgConfig = statusColors[entry.value] || { bg: '#f8fafc', border: '#e2e8f0' };
        const dotColor = statusColors?.[entry.value];//getLegendColor(entry.value);

        return (
          <div
            key={index}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: bgConfig.bg,
              border: `1px solid ${bgConfig.border}`,
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#374151',
            }}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: dotColor,
                borderRadius: '2px',
              }}
            />
            {entry.value}
          </div>
        );
      })}
    </div>
  );
};

export const StackedBarChart = ({commonProps,widget,data, dynaDataKey }:any) =>{
    const statusColors: Record<string, string> = widget.colors;
    const dataKey = widget.dataKey ? widget.dataKey : dynaDataKey;
    const itemHeight = 40; // Height per bar
    const minHeight = 500;
    const calculatedHeight = Math.max(minHeight, commonProps.data.length * itemHeight);
    const theme = useTheme();

    // Calculate left margin based on actual Y-axis label width
    const yAxisWidth = 300; // Fixed Y-axis width
    const maxLabelLength = Math.max(
      ...commonProps.data.map((d: any) => (d[widget.xKey] || '').toString().length)
    );
    // Estimate label content width (~8px per character) + padding
    const estimatedLabelContentWidth = maxLabelLength * 8 + 20;
    // Left margin = negative of overflow beyond yAxisWidth
    const dynamicLeftMargin = Math.max(-220, -(yAxisWidth - estimatedLabelContentWidth));
    
    return (
      <ResponsiveContainer width="100%" height={calculatedHeight}>
        <BChart 
        data={commonProps.data} 
        layout="vertical" 
        margin={{left: dynamicLeftMargin, right: 20}}
        barCategoryGap="20%" 
        >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#6b7280" fontSize={12}  interval={0}  tickFormatter={formatAxisValue} />
            <YAxis type="category" dataKey={widget.xKey} stroke="#6b7280" interval={0} fontSize={12} width={300} 
              axisLine={{ stroke: '#e5e7eb' }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
            content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0]?.payload;
                return (
                <div style={{
                    background: theme.darkMode ? '#1a1a1a' : 'white',
                    border: `1px solid ${theme.darkMode ? '#333' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>{label}</div>
                    {dataKey.map((key: string,index:number) => (
                    (row[key] > 0 || row[key] <= 0) && (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '10px', marginBottom: '4px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: getMultiBarColor(statusColors,key,row,widget.fields,index) }} />
                            {key}
                        </span>
                        <span style={{ fontWeight: 400 }}> 
                            {(() => {
                              const val = row[key];
                              return formatCellValue(val, widget.isPricing === true);
                            })()}
                          </span>
                        </div>
                    )
                    ))}

                    {row.total && 
                      <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px', fontWeight: 500 }}>
                      Total: {(() => {
                        const val = row.total;
                        return formatCellValue(val, widget.isPricing === true);
                      })()}
                      </div>
                    }
                </div>
                );
            }}
            />
            {dataKey?.map((key: string) => (
            <Bar
                key={key}
                dataKey={key}
                stackId={ 'stack' }
                fill={statusColors[key]}
                name={key}
            >
                {widget.fields && data?.map((entry:any, index:any) => (
                    <Cell key={`cell-${index}`} fill={getMultiBarColor(statusColors,key,entry,widget.fields,index)} />
                ))}
            </Bar>
            ))}
            <Legend content={
                <CustomLegend 
                        widget={widget} 
                        data={data} 
                        statusColors={statusColors} 
                    />
          }/>
        </BChart>
            
        </ResponsiveContainer>
    )

}
